<template>
  <div class="supplier-card__inner">
    <div class="supplier-card__scroll">
      <div class="supplier-card__scroll-content">
        <header class="supplier-card__header">
      <div class="company-title">
        <strong>{{ supplier.name }}</strong>
        <span
          v-for="tag in supplier.tags"
          :key="tag.text"
          class="supplier-tag"
          :class="'supplier-tag--' + tag.type"
        >
          {{ tag.text }}
        </span>
      </div>

      <div class="supplier-stats">
        <span v-for="stat in supplier.stats" :key="stat.label">
          {{ stat.label }}：<b>{{ stat.value }}</b>
        </span>
        <span>入驻时间：<b>{{ supplier.joinDate }}</b></span>
      </div>
        </header>

        <template v-if="supplier.expanded">
          <nav class="detail-tabs">
        <button
          v-for="tab in supplier.detailTabs"
          :key="tab"
          type="button"
          :class="{ active: supplier.activeTab === tab }"
          @click="$emit('select-tab', tab)"
        >
          {{ tab }}
        </button>
          </nav>

          <div class="supplier-detail">
        <template v-if="supplier.activeTab === '基础信息'">
          <div class="info-row"><span>公司名称：</span>{{ supplier.fullName }}（{{ supplier.code }}）</div>
          <div class="info-row"><span>公司地址：</span>{{ supplier.address }}</div>
          <div class="info-row"><span>联系人：</span>{{ supplier.contact }}</div>
          <div class="info-row"><span>手机：</span>{{ supplier.phone }}</div>
          <div class="info-row"><span>公司类型：</span>{{ supplier.types }}</div>
          <div class="license-row">
            <span>营业执照：</span>
            <div v-if="supplier.masked" class="attachment-empty">******</div>
            <div v-else-if="enterpriseLicenseImages.length" class="attachment-image-list">
              <el-image
                v-for="imageUrl in enterpriseLicenseImages"
                :key="imageUrl"
                class="attachment-image attachment-image--license"
                :src="imageUrl"
                :preview-src-list="enterpriseLicenseImages"
                fit="cover"
                lazy
              />
            </div>
            <div v-else class="attachment-empty">暂无</div>
          </div>
          <template v-if="!supplier.isCustomer">
            <div class="brand-row">
              <span>{{ supplier.agentBrandLabel }}：</span>
              <div class="brand-row__content">{{ supplier.agentBrands }}</div>
            </div>
            <div class="brand-row">
              <span>{{ supplier.businessBrandLabel }}：</span>
              <div class="brand-row__content">{{ supplier.businessBrands }}</div>
            </div>
          </template>
        </template>
        <template v-else-if="supplier.activeTab === '企业信息'">
          <div class="info-row"><span>成立日期：</span>{{ detailValue('establishedTime') }}</div>
          <div class="info-row"><span>法人姓名：</span>{{ detailValue('legalPersonName') }}</div>
          <div class="info-row"><span>法人电话：</span>{{ detailValue('legalPersonPhone') }}</div>
          <div class="info-row"><span>注册资本：</span>{{ detailValue('registeredCapital') }}</div>
          <div class="info-row"><span>员工人数：</span>{{ detailValue('staffNum') }}</div>
          <div class="info-row"><span>经营范围：</span>{{ detailValue('businessScope') }}</div>
          <div class="info-row"><span>企业网址：</span>{{ detailValue('website') }}</div>
        </template>
        <template v-else-if="supplier.activeTab === '公司资质'">
          <!-- 公司资质 Tab 只展示已上传的图片，不输出文字字段、上传状态或空值占位。 -->
          <div v-if="companyQualificationImages.length" class="qualification-gallery">
            <el-image
              v-for="imageUrl in companyQualificationImages"
              :key="imageUrl"
              class="attachment-image attachment-image--qualification"
              :src="imageUrl"
              :preview-src-list="companyQualificationImages"
              fit="cover"
              lazy
            />
          </div>
        </template>
        <template v-else-if="supplier.activeTab === '公司照片'">
          <!-- 公司照片只展示真实上传的车间/办公环境图片，不再输出“已上传”文字或企业 Logo。 -->
          <div v-if="supplier.masked" class="attachment-empty">******</div>
          <div v-else-if="companyPhotoImages.length" class="qualification-gallery">
            <el-image
              v-for="imageUrl in companyPhotoImages"
              :key="imageUrl"
              class="attachment-image attachment-image--qualification"
              :src="imageUrl"
              :preview-src-list="companyPhotoImages"
              fit="cover"
              lazy
            />
          </div>
          <div v-else class="attachment-empty">暂无</div>
        </template>
        <template v-else-if="supplier.activeTab === '联系人'">
          <div v-if="supplier.masked" class="attachment-empty">******</div>
          <div v-else-if="contactUsers.length" class="contact-list">
            <section v-for="contact in contactUsers" :key="contact.id" class="contact-item">
              <div class="contact-item__header">
                <strong>{{ contact.realName || contact.nickName || '未命名联系人' }}</strong>
                <span v-if="String(contact.defaultFlag) === '1'">默认联系人</span>
              </div>
              <div class="info-row"><span>客户手机号：</span>{{ contactMobile(contact) }}</div>
              <div class="info-row"><span>客户编码：</span>{{ contactValue(contact.customerNumber) }}</div>
              <div class="info-row"><span>归属销售/商务：</span>{{ contactValue(contact.userName) }}</div>
              <div class="info-row"><span>工作角色：</span>{{ contactValue(contact.jobRole) }}</div>
              <div class="info-row"><span>公司职位：</span>{{ contactValue(contact.jobPost) }}</div>
              <div class="info-row"><span>客户标签：</span>{{ contactValue(contact.relationLabel) }}</div>
            </section>
          </div>
          <div v-else class="attachment-empty">暂无</div>
        </template>
        <template v-else-if="supplier.activeTab === '采购偏好'">
          <div class="info-row"><span>采购偏好：</span>{{ supplier.purchasePreferences || '暂无' }}</div>
        </template>
        <template v-else-if="supplier.activeTab === '代理品牌'">
          <!-- 每个代理品牌单独展示名称及其证书图片，预览范围只包含该品牌自己的附件。 -->
          <div v-if="supplier.masked" class="attachment-empty">******</div>
          <div v-else-if="agentBrandCertificates.length" class="agent-brand-list">
            <section
              v-for="brand in agentBrandCertificates"
              :key="brand.id"
              class="agent-brand-item"
            >
              <div class="brand-row">
                <span>代理品牌：</span>
                <div class="brand-row__content">{{ brand.name }}</div>
              </div>
              <div v-if="brand.images.length" class="attachment-image-list agent-brand-item__images">
                <el-image
                  v-for="imageUrl in brand.images"
                  :key="imageUrl"
                  class="attachment-image attachment-image--qualification"
                  :src="imageUrl"
                  :preview-src-list="brand.images"
                  fit="cover"
                  lazy
                />
              </div>
              <div v-else class="attachment-empty">暂无代理证书图片</div>
            </section>
          </div>
          <div v-else class="attachment-empty">暂无</div>
        </template>
        <template v-else-if="supplier.activeTab === '经营品牌'">
          <div v-if="supplier.masked" class="attachment-empty">******</div>
          <div v-else-if="businessBrandList.length" class="business-brand-list">
            <span
              v-for="brand in businessBrandList"
              :key="brand.id"
              :title="brand.name"
              class="business-brand-item"
            >
              {{ brand.name }}
            </span>
          </div>
          <div v-else class="attachment-empty">暂无</div>
        </template>
        <div v-else-if="supplier.activeTab === '盘雷数据'">
          <div v-if="supplier.masked" class="attachment-empty">******</div>
          <div v-else-if="supplier.stats.length" class="panlei-statistics">
            <div v-for="stat in supplier.stats" :key="stat.label" class="panlei-statistics__item">
              <span>{{ stat.label }}</span>
              <b>{{ stat.value }}</b>
            </div>
          </div>
          <div v-else class="attachment-empty">暂无</div>
        </div>
        <div v-else class="tab-placeholder">
          暂无可展示的{{ supplier.activeTab }}数据
        </div>
          </div>
        </template>
      </div>
    </div>

    <button type="button" class="card-toggle" @click="$emit('toggle')">
      {{ supplier.expanded ? '收起' : '展开' }}
      <span :class="{ rotated: !supplier.expanded }">⌃</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'SupplierCardContent',
  props: {
    supplier: {
      type: Object,
      required: true
    }
  },
  emits: ['toggle', 'select-tab'],
  computed: {
    /** 基础信息中展示的营业执照图片地址。 */
    enterpriseLicenseImages() {
      return this.parseAttachmentImages(this.supplier.raw && this.supplier.raw.enterpriseLicense)
    },

    /**
     * 公司资质 Tab 的全部图片。
     * 合并企业资质和营业执照，并按 URL 去重，防止同一附件在两个字段重复展示。
     */
    companyQualificationImages() {
      if (this.supplier.masked) return []
      const qualificationImages = this.parseAttachmentImages(
        this.supplier.raw && this.supplier.raw.enterpriseQualification
      )
      return [...new Set(qualificationImages.concat(this.enterpriseLicenseImages))]
    },

    /** 公司照片 Tab 的车间、办公环境图片；企业 Logo 按页面要求不参与展示。 */
    companyPhotoImages() {
      return this.parseAttachmentImages(this.supplier.raw && this.supplier.raw.officeWorkshopEnvironment)
    },

    /**
     * 代理品牌及其对应证书图片。
     * 保留无图片的品牌名称，便于用户确认该品牌已被收录；图片仅从该品牌 certificateFiles 解析。
     */
    agentBrandCertificates() {
      if (this.supplier.masked) return []
      const brands = this.supplier.raw && Array.isArray(this.supplier.raw.agentBrands)
        ? this.supplier.raw.agentBrands
        : []
      return brands.map((brand, index) => ({
        id: brand.id || `${brand.brandId || 'agent-brand'}-${index}`,
        name: brand.brandName || '未命名品牌',
        images: this.parseAttachmentImages(brand.certificateFiles)
      }))
    },

    /**
     * 经营品牌按接口数组逐项展示，不再拼接为一段文本，避免长数据被误认为未显示完整。
     * Mock 或历史数据未返回数组时，才兼容原有斜杠文本。
     */
    businessBrandList() {
      if (this.supplier.masked) return []
      const brands = this.supplier.raw && this.supplier.raw.businessBrands
      if (Array.isArray(brands)) {
        return brands.map((brand, index) => ({
          id: brand.id || `${brand.brandId || 'business-brand'}-${index}`,
          name: brand.brandName || '未命名品牌'
        }))
      }
      return String(this.supplier.businessBrands || '')
        .split('/')
        .filter(Boolean)
        .map((name, index) => ({ id: `business-brand-text-${index}`, name }))
    },

    /** 联系人由公共供应商接口的 userList 返回，保留后端排序及默认联系人标识。 */
    contactUsers() {
      const users = this.supplier.raw && this.supplier.raw.userList
      return Array.isArray(users) ? users : []
    }
  },
  methods: {
    /** 统一处理联系人可能缺失的展示字段，避免空值造成各行错位。 */
    contactValue(value) {
      return value === null || value === undefined || value === '' ? '暂无' : value
    },

    /** getMyEnterprise 同时返回绑定手机号和手机号码，优先展示绑定手机号。 */
    contactMobile(contact) {
      return this.contactValue(contact.bindPhone || contact.mobilePhone)
    },

    /**
     * 读取后端详情字段。
     * 脱敏记录不包含 raw 详情，这里统一展示星号，避免不同 Tab 出现不一致的空值文案。
     */
    detailValue(field) {
      if (this.supplier.masked) return '******'
      const value = this.supplier.raw && this.supplier.raw[field]
      return value === null || value === undefined || value === '' ? '暂无' : value
    },

    /**
     * 把后端附件字段统一转换为可预览的图片 URL 数组。
     *
     * <p>兼容 JSON 字符串、数组、单个文件对象和直接 URL；同时识别 url、fileUrl、src、path
     * 四种项目历史字段。PDF、Word、Excel 等非图片附件不进入图片列表。</p>
     */
    parseAttachmentImages(value) {
      if (this.supplier.masked || value === null || value === undefined || value === '') return []

      let files = value
      if (typeof files === 'string') {
        const text = files.trim()
        if (!text || text === '[]') return []
        try {
          files = JSON.parse(text)
        } catch (error) {
          // 历史数据可能直接保存 URL，JSON 解析失败时仍按单个附件处理。
          files = [text]
        }
      }

      if (!Array.isArray(files)) files = [files]

      const imageUrls = files
        .map(file => {
          if (typeof file === 'string') return { url: file, fileType: '' }
          if (!file || typeof file !== 'object') return null
          return {
            url: file.url || file.fileUrl || file.src || file.path || '',
            fileType: file.fileType || file.type || file.mimeType || ''
          }
        })
        .filter(file => file && file.url && this.isImageAttachment(file))
        .map(file => file.url)

      return [...new Set(imageUrls)]
    },

    /** 排除明确的非图片文件；没有扩展名的 OSS 签名地址按图片兼容展示。 */
    isImageAttachment(file) {
      const fileType = String(file.fileType || '').toLowerCase()
      const urlWithoutQuery = String(file.url).split('?')[0].toLowerCase()
      const nonImagePattern = /(pdf|word|docx?|excel|xlsx?|pptx?|zip|rar)/
      return !nonImagePattern.test(fileType) &&
        !/\.(pdf|docx?|xlsx?|pptx?|zip|rar)$/.test(urlWithoutQuery)
    },

  }
}
</script>
